import type { ReactNode } from 'react'
import type { PaginationProps } from '../Pagination'
import type { SearchProps } from '../Search'

export type DataTableState = 'default' | 'loading' | 'empty' | 'error'

export type DataTableSortDirection = 'asc' | 'desc'

export type DataTableColumn<T extends DataTableRow = DataTableRow> = {
  id: string
  label: string
  sortable?: boolean
  showInfo?: boolean
  width?: number | string
  colSpan?: number
  align?: 'left' | 'center' | 'right'
  renderCell?: (row: T) => ReactNode
}

export type DataTableRow = {
  id: string
  [key: string]: ReactNode | string | number | boolean | undefined
}

export type DataTableEmptyState = {
  title?: string
  description?: string
  actionLabel?: string
  onAction?: () => void
}

export type DataTableErrorState = {
  title?: string
  description?: string
  actionLabel?: string
  onAction?: () => void
}

export type DataTablePaginationProps = PaginationProps

export type DataTableSearchProps = Omit<SearchProps, 'previewState'>

export type DataTableGroup<T extends DataTableRow = DataTableRow> = {
  id: string
  title: string
  rows: T[]
}

export type DataTableExpandedTableConfig<
  T extends DataTableRow = DataTableRow,
  P extends DataTableRow = DataTableRow,
> = {
  columns: DataTableColumn<T>[]
  getRows: (parentRow: P) => T[]
  /**
   * Child rows can expand again (billing invoice → ledger → procedures).
   * Uses the parent table's `expandedRowIds` / `onExpandedRowIdsChange`.
   * Only one nested row per parent is expanded at a time.
   */
  expandable?: boolean
  /** Column that shows the right-aligned expand control. Defaults to the first column. */
  expandableColumnId?: string
  /** Nested table rendered when a child row expands. */
  expandedTable?: DataTableExpandedTableConfig<DataTableRow, T>
  /** Detail panel when a child row expands and no nested `expandedTable` is set. */
  renderExpandedContent?: (row: T) => ReactNode
  showActions?: boolean
}

export type DataTableProps<T extends DataTableRow = DataTableRow> = {
  columns: DataTableColumn<T>[]
  rows: T[]
  /** Grouped expandable rows with nested child table. When set, `rows` is ignored. */
  groups?: DataTableGroup<T>[]
  state?: DataTableState
  selectable?: boolean
  selectedRowIds?: string[]
  onSelectedRowIdsChange?: (ids: string[]) => void
  /**
   * Flat-row expand for a detail panel (Title / Title). Ignored when `groups` is set.
   * Only one row per nesting level is expanded at a time.
   */
  expandable?: boolean
  /**
   * Column id that hosts the right-aligned expand control (billing pattern).
   * Defaults to the first data column when `expandable` is true.
   */
  expandableColumnId?: string
  expandedRowIds?: string[]
  onExpandedRowIdsChange?: (ids: string[]) => void
  /** Nested table with its own header row (billing ledger pattern). */
  expandedTable?: DataTableExpandedTableConfig<DataTableRow, T>
  renderExpandedContent?: (row: T) => ReactNode
  showActions?: boolean
  actionsLabel?: string
  renderActions?: (row: T) => ReactNode
  /** Optional search field above the table. Omit to hide. */
  search?: DataTableSearchProps
  pagination?: DataTablePaginationProps
  emptyState?: DataTableEmptyState
  errorState?: DataTableErrorState
  sortColumnId?: string
  sortDirection?: DataTableSortDirection
  onSort?: (columnId: string) => void
  onRowClick?: (row: T) => void
  className?: string
  /** Dark theme. */
  darkMode?: boolean
  /** Static visual state for the design-system showcase only. */
  previewState?: DataTableState
}

export const DATA_TABLE_STATES: DataTableState[] = [
  'default',
  'loading',
  'empty',
  'error',
]

export const DATA_TABLE_STATE_LABELS: Record<DataTableState, string> = {
  default: 'Default',
  loading: 'Loading',
  empty: 'Empty',
  error: 'Error',
}

export const DATA_TABLE_SHOWCASE_VARIANTS = [
  { id: 'default', label: 'Default' },
  { id: 'checkbox-pagination', label: 'Checkbox + pagination' },
  { id: 'search', label: 'Search + checkbox + pagination' },
  { id: 'pagination-info', label: 'Pagination + info' },
  { id: 'switch-badges', label: 'Switch + badges' },
  { id: 'expanded', label: 'Expanded + detail panel' },
  { id: 'checkbox-expanded', label: 'Checkbox + detail panel' },
  { id: 'expanded-nested-header', label: 'Expanded + nested header table' },
  {
    id: 'expanded-nested-double',
    label: 'Expanded + nested expandable rows',
  },
  { id: 'grouped-expanded', label: 'Grouped + nested table' },
] as const

export type DataTableShowcaseVariantId =
  (typeof DATA_TABLE_SHOWCASE_VARIANTS)[number]['id']
